import {
    type AEMTrackingDataLayerType,
    getDealerAEMTrackingInfo,
    trackEvent,
} from "../../common-deprecated/utils/tracking";
import type { DealerResultType } from "../../common-deprecated/utils/dealerConstants";
import type { TradeInAemModelSelectReducerType } from "../redux/reducers/TradeInAemModelSelectReducer";
import {
    type TradeInAemIntegratedParentComponentType,
    type TradeInAemModelSelectType,
    type TradeInAemSearchType,
    TradeInAemNavStep,
    TradeInAemVersion,
} from "./constants";
import type { TradeInCsl } from "../csl/TradeInCsl";
import { COMPONENT_BUILDANDBUY, COMPONENT_CAR_CONFIG } from "../../shared-logic/server/components";

// ----------------------------------------------------------------------
// Utils
// ----------------------------------------------------------------------

export type TradeInTrackingParamsType = {
    csl: TradeInCsl;
    dealer: DealerResultType | null;
    tradeInVersion: TradeInAemVersion;
    parentComponent: TradeInAemIntegratedParentComponentType | null;
};

type TrackingComponentType = "tradein-calculator" | "tradein-request";
type TrackingVariantType = "dealer" | "standalone" | "carconfig" | "carconfig-apheleia";

const getTrackingComponent = (trackingParams: TradeInTrackingParamsType): TrackingComponentType => {
    const { csl } = trackingParams;
    // If the user has to provide contact details it should be "tradein-request"
    if (csl.enabledNavSteps.includes(TradeInAemNavStep.ContactForm)) return "tradein-request";
    return "tradein-calculator";
};

const getTrackingVariant = (trackingParams: TradeInTrackingParamsType): TrackingVariantType => {
    const { tradeInVersion } = trackingParams;
    switch (tradeInVersion) {
        case TradeInAemVersion.Integrated:
            return getIntegratedTrackingVariant(trackingParams);
        case TradeInAemVersion.Hidden:
            return "dealer";
        case TradeInAemVersion.Standalone:
            return "standalone";
    }
};

const getIntegratedTrackingVariant = (trackingParams: TradeInTrackingParamsType): TrackingVariantType => {
    const { parentComponent } = trackingParams;
    switch (parentComponent) {
        case COMPONENT_BUILDANDBUY:
            return "carconfig";
        case COMPONENT_CAR_CONFIG:
            return "carconfig-apheleia";
        default:
            return "carconfig";
    }
};

const getTrackingVehicleType = (type: TradeInAemModelSelectType | null): string => (type ? `${type}-cars` : "");

const getTrackingProductData = (
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
): AEMTrackingDataLayerType["product"] | undefined => {
    const selectedModel = tradeInAemModelSelect.modelList.find(
        (model) => model.id === tradeInAemModelSelect.selectedModelId,
    );
    return [
        {
            category: "cars",
            subCategory: getTrackingVehicleType(tradeInAemModelSelect.selectedCarType),
            productInfo: selectedModel
                ? {
                      tmeModelId: selectedModel?.code?.toLowerCase() || selectedModel.id, // fallbacking to id just in case
                      modelName: selectedModel.name,
                  }
                : undefined,
        },
    ];
};

// ----------------------------------------------------------------------
// Events
// ----------------------------------------------------------------------

export const trackStepFindVehicle = (trackingParams: TradeInTrackingParamsType): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: "my-vehicle",
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "my-vehicle",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepVehicleDetails = (
    trackingParams: TradeInTrackingParamsType,
    searchType: TradeInAemSearchType,
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `my-vehicle/${searchType}/selected`, // TODO: searchType will probably not be correct once we support more than licencePlate,
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "my-vehicle-selected",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepVehicleValue = (
    trackingParams: TradeInTrackingParamsType,
    searchType: TradeInAemSearchType,
): void => {
    const isFirstValuation = trackingParams.csl.enabledNavSteps.includes(TradeInAemNavStep.Summary);
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `my-vehicle/${searchType}/valuation`, // TODO: searchType will probably not be correct once we support more than licencePlate,
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: isFirstValuation ? "first-valuation" : "my-vehicle-valuation",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepModelSelect = (trackingParams: TradeInTrackingParamsType): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: "car-select/type",
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "car-select/type",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepDealerFinder = (
    trackingParams: TradeInTrackingParamsType,
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `select-dealer/${getTrackingVehicleType(tradeInAemModelSelect?.selectedCarType)}`,
        },
        {
            product: getTrackingProductData(tradeInAemModelSelect),
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "select-dealer",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepContactForm = (
    trackingParams: TradeInTrackingParamsType,
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `contact-details/${getTrackingVehicleType(tradeInAemModelSelect?.selectedCarType)}`,
        },
        {
            product: getTrackingProductData(tradeInAemModelSelect),
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "contact-details",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackStepSummary = (
    trackingParams: TradeInTrackingParamsType,
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
    hashedEmailAddress: string,
): void => {
    const { dealer } = trackingParams;
    const hasUserInfo = !!hashedEmailAddress;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `quotation-completed/${getTrackingVehicleType(tradeInAemModelSelect?.selectedCarType)}`,
        },
        {
            product: getTrackingProductData(tradeInAemModelSelect),
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "quotation-completed",
                conversion: 1,
                variant: getTrackingVariant(trackingParams),
            },
            userinfo: hasUserInfo
                ? {
                      em: hashedEmailAddress,
                  }
                : undefined,
        },
    );
};

/** searchType did not have any values specified in the tracking specs. I arbitrarily chose "select" here */
type TrackingSearchType = TradeInAemSearchType | "select";

export const trackStartSearch = (trackingParams: TradeInTrackingParamsType, searchType: TrackingSearchType): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `my-vehicle/${searchType}`,
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "my-vehicle-search",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackFinishedSearch = (
    trackingParams: TradeInTrackingParamsType,
    searchType: TrackingSearchType,
    state: "found" | "notfound" | "error" | "insufficient",
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `my-vehicle/${searchType}/${state}`,
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "my-vehicle-search-results",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackSelectVehicleType = (
    trackingParams: TradeInTrackingParamsType,
    vehicleType: TradeInAemModelSelectType,
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "workflowevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "workflow-step",
            label: `car-select/model/${getTrackingVehicleType(vehicleType)}`,
        },
        {
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
            workflow: {
                name: getTrackingComponent(trackingParams),
                step: "car-select/model",
                conversion: 0,
                variant: getTrackingVariant(trackingParams),
            },
        },
    );
};

export const trackOpenPdf = (
    trackingParams: TradeInTrackingParamsType,
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "componentevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: "download-tradein-quote",
            label: "pdf",
        },
        {
            product: getTrackingProductData(tradeInAemModelSelect),
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
        },
    );
};

export const trackSummaryDealerAction = (
    trackingParams: TradeInTrackingParamsType,
    tradeInAemModelSelect: TradeInAemModelSelectReducerType,
    action: "call" | "email" | "chat" | "site",
): void => {
    const { dealer } = trackingParams;

    trackEvent(
        {
            name: "componentevent",
            component: getTrackingComponent(trackingParams),
            group: "product-general",
            action: action === "site" ? "open-dealersite" : `${action}-dealer`,
            label: dealer?.name,
        },
        {
            product: getTrackingProductData(tradeInAemModelSelect),
            dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
        },
    );
};
